import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../../contexts/auth-context.js';

/**
 * MINI MAP ZONE COMPONENT
 * 
 * Renders a small SVG map for queue time or transaction time zones.
 * Dynamically loads the correct SVG component based on venue and type.
 * Displays time in minutes as the label instead of occupancy count.
 * Supports multiple shapes with individual time values per shape id.
 */

const MiniMapZone = ({
  type,              // "queue" or "transaction"
  currentLocation,   // location config from locations.json
  timeData,          // object keyed by shape id: { 1: 3, 2: 5 } (queue: minutes, transaction: seconds)
  label,             // optional override label (defaults to zone config label)
  compact = false,   // hide the title (the Map tab's side rail already names the view)
}) => {
  const authCtx = useContext(AuthContext);
  const [SvgComponent, setSvgComponent] = useState(null);

  // Get zone config based on type
  const zoneConfig = type === "queue" 
    ? currentLocation.queueZone 
    : currentLocation.transactionZone;

  // Load SVG component dynamically based on venue and type
  useEffect(() => {
    // If no zone config exists, don't load
    if (!zoneConfig) return;

    const loadSvgComponent = async () => {
      try {
        const componentName = type === "queue" 
          ? "QueueZoneSvg" 
          : "TransactionZoneSvg";
        
        // Capitalize first letter of venue for component naming
        const venueCapitalized = authCtx.venue.charAt(0).toUpperCase() + authCtx.venue.slice(1);
        
        const module = await import(
          `../../clients/${authCtx.league}/${authCtx.venue}/maps/${venueCapitalized}${componentName}.js`
        );
        setSvgComponent(() => module.default);
      } catch (error) {
        console.error(`Error loading ${type} zone SVG:`, error);
      }
    };

    if (authCtx.venue && authCtx.league) {
      loadSvgComponent();
    }
  }, [authCtx.venue, authCtx.league, type, zoneConfig]);

  // If no zone config exists, don't render
  if (!zoneConfig) return null;

  // Get color based on time value in minutes (lower is better for wait times)
  const getTimeColor = (minutes) => {
    if (minutes === null || minutes === undefined) return "rgb(220, 220, 220)";
    if (minutes <= 2) return "rgb(122, 219, 92)";   // Green - very short wait
    if (minutes <= 5) return "rgb(245, 235, 84)";   // Yellow - moderate wait
    if (minutes <= 10) return "rgb(234, 170, 60)";  // Orange - longer wait
    return "rgb(218, 81, 70)";                       // Red - long wait
  };

  // Get raw time value for a specific shape id
  const getTimeValue = (shapeId) => {
    if (!timeData || timeData[shapeId] === undefined) return null;
    return timeData[shapeId];
  };

  // Get time value in minutes for color calculation
  const getMinutesForColor = (shapeId) => {
    const value = getTimeValue(shapeId);
    if (value === null || value === undefined) return null;
    
    if (type === 'transaction') {
      // Transaction time is in seconds, convert to minutes
      return Math.round(value / 60);
    }
    // Queue time is already in minutes
    return value;
  };

  // Format time for display
  const formatTimeDisplay = (shapeId) => {
    const value = getTimeValue(shapeId);
    if (value === null || value === undefined) return "--";
    
    if (type === 'transaction') {
      // Convert seconds to "M:SS" format
      const minutes = Math.floor(value / 60);
      const seconds = value % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    
    // Queue time is already in minutes, just return the number
    return `${value}`;
  };

  // Render zone shape(s)
  const renderZoneShapes = () => {
    const shapes = zoneConfig.shapes || [];
    
    return shapes.map((shape) => {
      const minutesValue = getMinutesForColor(shape.id);
      const fillColor = getTimeColor(minutesValue);
      
      if (shape.type === "path") {
        return (
          <path
            key={shape.id}
            d={shape.d}
            fill={fillColor}
            style={{ transition: "fill 0.2s ease-in-out" }}
          />
        );
      } else if (shape.type === "polygon") {
        return (
          <polygon
            key={shape.id}
            points={shape.points?.join(" ")}
            fill={fillColor}
            style={{ transition: "fill 0.2s ease-in-out" }}
          />
        );
      } else if (shape.type === "rect") {
        return (
          <rect
            key={shape.id}
            x={shape.x}
            y={shape.y}
            width={shape.width}
            height={shape.height}
            fill={fillColor}
            style={{ transition: "fill 0.2s ease-in-out" }}
          />
        );
      }
      return null;
    });
  };

  // Render time labels for each shape
  const renderTimeLabels = () => {
    const shapes = zoneConfig.shapes || [];
    
    return shapes.map((shape) => {
      // Skip if no labelPosition defined
      if (!shape.labelPosition) return null;
      
      const { x, y } = shape.labelPosition;
      const displayValue = formatTimeDisplay(shape.id);
      
      return (
        <text
          key={`label-${shape.id}`}
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fontSize: '40px',
            fontWeight: 'bold',
            fill: '#000',
            pointerEvents: 'none'
          }}
        >
          {displayValue}
        </text>
      );
    });
  };

  if (!SvgComponent) {
    return <div className="text-center p-3">Loading...</div>;
  }

  return (
    <div className="mini-map-zone" style={compact ? { height: '100%' } : undefined}>
      {/* Title */}
      {!compact && (
        <div className="text-center mb-2">
          <span style={{ fontSize: '16px', fontWeight: '600' }}>
            {label || zoneConfig.label}
          </span>
          <span style={{ fontSize: '16px', color: '#666', marginLeft: '8px' }}>
            ({zoneConfig.unit})
          </span>
        </div>
      )}
      
      {/* SVG Container: keeps the drawing's shape (from svgWidth/svgHeight) so it can't squash */}
      <div
        className={compact ? 'mini-map-zone-fit' : undefined}
        style={{
          width: '100%',
          maxWidth: '450px',
          margin: '0 auto',
          ...(zoneConfig.svgWidth && zoneConfig.svgHeight
            ? { aspectRatio: `${zoneConfig.svgWidth} / ${zoneConfig.svgHeight}` }
            : {}),
        }}
      >
        <SvgComponent>
          {renderZoneShapes()}
          {renderTimeLabels()}
        </SvgComponent>
      </div>
    </div>
  );
};

export default MiniMapZone;